<template>
  <div :class="menuClasses">
    <div class="flex flex-col gap-2">
      <div
        :class="[
          'flex p-2',
          navBar.shown
            ? 'flex-row items-center justify-between gap-2'
            : 'flex-col-reverse items-center gap-2',
        ]"
      >
        <ThemeSwitcher v-model="isDark" />

        <PanelLeftOpen
          class="cursor-pointer rounded-sm text-[var(--p-primary-color)] hover:bg-surface-100 dark:text-[var(--p-primary-color)] dark:hover:bg-surface-800"
          @click="navBar.toggle"
        />
      </div>

      <div class="flex w-full items-center justify-center py-4">
        <img
          src="@/assets/LogoManagement.svg"
          class="w-32 dark:brightness-0 dark:invert"
          alt="logo"
        />
      </div>

      <div v-if="navBar.shown" class="flex items-center justify-center text-color">
        <span class="font-bold">
          {{ languageStore.t('general.greeting') }}
          <span class="italic">{{ userName }}!</span>
        </span>
      </div>
      <div v-if="navBar.shown" class="w-full px-1 flex flex-col gap-1">
        <span v-if="isChangingClient" class="text-xs text-sky-600 flex items-center gap-1 mb-1">
          <i class="pi pi-spin pi-spinner" />
          {{ languageStore.t('general.changingClient') }}
        </span>
        <ClientSelect
          v-model="selectedClientId"
          :disabled="isChangingClient"
          @change="onClientChange"
        />
      </div>
    </div>

    <div class="flex w-full flex-col gap-4 overflow-hidden overflow-y-auto">
      <PanelMenu
        :model="permissionsNavbarRouter"
        :class="[navBar.shown ? '' : 'flex items-center justify-center']"
      >
        <template #item="{ item, props }">
          <router-link v-if="item.to" v-slot="{ href, navigate }" :to="item.to" custom>
            <a
              :href="href"
              v-bind="props.action"
              @click="navigate"
              class="flex items-center gap-2"
              :class="isMultilineItem(item) ? 'min-h-10 py-2' : 'h-10'"
            >
              <Icons :icon="item.icon" class="shrink-0 font-bold" />

              <span
                v-if="navBar.shown"
                class="font-bold"
                :class="isMultilineItem(item) ? 'whitespace-normal break-words leading-snug' : ''"
              >
                {{ languageStore.t(item.label as string) }}
              </span>
            </a>
          </router-link>

          <a
            v-else
            v-bind="props.action"
            class="flex items-center justify-between gap-2"
            :class="isMultilineItem(item) ? 'min-h-10 py-2' : 'h-10'"
            v-if="navBar.shown"
          >
            <div class="flex items-center gap-2">
              <Icons :icon="item.icon" class="shrink-0 font-bold" />

              <span
                v-if="navBar.shown"
                class="font-bold"
                :class="isMultilineItem(item) ? 'whitespace-normal break-words leading-snug' : ''"
              >
                {{ languageStore.t(item.label as string) }}
              </span>
            </div>

            <Icons
              v-if="navBar.shown && hasChildren(item)"
              icon="chevron-down"
              class="shrink-0 font-bold"
            />
          </a>
        </template>
      </PanelMenu>
    </div>

    <div v-if="navBar.shown" class="bottom-2 flex flex-col items-center gap-4">
      <div class="flex items-center justify-center gap-2">
        <div
          class="flex cursor-pointer items-center gap-2 rounded-md bg-sky-100 px-3 py-2 hover:bg-sky-200 transition-colors"
          @click="showProfileDialog = true"
        >
          <UserRound :size="16" :stroke-width="2.5" color="#03619C" />
          <span class="font-bold text-sky-800">{{ languageStore.t('general.myAccount') }}</span>
        </div>
        <div
          class="flex cursor-pointer items-center gap-2 rounded-md bg-sky-100 px-3 py-2 hover:bg-sky-200 transition-colors"
          @click="logout"
        >
          <LogOut :size="16" :stroke-width="2.5" color="#03619C" />
          <span class="font-bold text-sky-800">{{ languageStore.t('general.exit') }}</span>
        </div>
      </div>

      <div class="flex flex-col items-center justify-center gap-6">
        <SelectLanguage class="mx-2" />

        <div class="flex flex-row items-center justify-center gap-2">
          <span class="text-sm font-bold text-color">Version: {{ appVersion }}</span>
          <!-- <span class="text-sm font-bold text-[var(--p-primary-color)]">API version: {{ apiVersionLabel }}</span> -->
        </div>
      </div>
    </div>
  </div>

  <Dialog
    v-model:visible="showProfileDialog"
    :header="languageStore.t('general.myAccount')"
    modal
    class="!w-[90vw] lg:!w-[28rem]"
  >
    <div class="flex flex-col items-center gap-4">
      <div class="flex h-16 w-16 items-center justify-center rounded-full bg-sky-100">
        <UserRound :size="32" :stroke-width="1.5" color="#03619C" />
      </div>
      <div class="text-center">
        <p class="text-xl font-bold text-gray-800">{{ currentUser?.name }}</p>
        <Tag severity="info" :value="getUserRoleLabel(currentUser?.userRole)" class="mt-1" />
      </div>
    </div>
    <Divider />
    <div class="flex flex-col gap-3">
      <div class="flex items-center gap-3">
        <i class="pi pi-envelope text-sky-700" />
        <div>
          <p class="text-xs font-medium text-gray-500">{{ languageStore.t('general.email') }}</p>
          <p class="text-sm text-gray-800">{{ currentUser?.email || '—' }}</p>
        </div>
      </div>
      <div class="flex items-center gap-3">
        <i class="pi pi-info-circle text-sky-700" />
        <div>
          <p class="text-xs font-medium text-gray-500">{{ languageStore.t('general.description') }}</p>
          <p class="text-sm text-gray-800">{{ currentUser?.description || '—' }}</p>
        </div>
      </div>
      <div class="flex items-center gap-3">
        <i class="pi pi-bell text-sky-700" />
        <div>
          <p class="text-xs font-medium text-gray-500">{{ languageStore.t('general.receiveEmails') }}</p>
          <p class="text-sm text-gray-800">{{ currentUser?.receiveEmails ? languageStore.t('general.yes') : languageStore.t('general.no') }}</p>
        </div>
      </div>
    </div>
    <template #footer>
      <Button :label="languageStore.t('general.edit')" icon="pi pi-pencil" variant="outlined" @click="openProfileEdit" />
    </template>
  </Dialog>

  <UserUpdateModal
    v-if="showProfileEditModal"
    :showModal="showProfileEditModal"
    :user="currentUser"
    :loading="isUpdatingProfile"
    :is-self-edit="true"
    @close="showProfileEditModal = false"
    @save="handleProfileUpdate"
  />
</template>

<script setup lang="ts">
import ThemeSwitcher from '@/components/ThemeSwitcher .vue'
import Icons from '@/components/subcomponents/Icons.vue'
import UserUpdateModal from '@/components/userControl/UserUpdateModal.vue'
import { hasPermission } from '@/composables/RequiredPermissions'
import { useDarkMode } from '@/composables/useDarkMode'
import { UserPermission } from '@/models/enums/permissions'
import { INavBarItem } from '@/models/navbar'
import { getAppVersion } from '@/services/appService'
import { getBackendVersion } from '@/services/versionService'
import { IUser, getUserRoleLabel } from '@/models/user'
import { buildUserDto } from '@/composables/useUserDtoBuilder'
import * as ls from '@/services/localStorage'
import * as userService from '@/services/userService'
import * as userClientService from '@/services/userClientService'
import { useLanguageStore } from '@/stores/LanguageStore'
import { useNavBarStore } from '@/stores/NavBarStore'
import { useClientStore } from '@/stores/ClientStore'
import { LogOut, PanelLeftOpen, UserRound } from 'lucide-vue-next'
import { Button, Dialog, Divider, PanelMenu, Tag, useToast } from 'primevue'
import type { MenuItem } from 'primevue/menuitem'
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import ClientSelect from './ClientSelect.vue'
import SelectLanguage from '../subcomponents/SelectLanguage.vue'

const router = useRouter()

const navBar = useNavBarStore()

const languageStore = useLanguageStore()
const clientStore = useClientStore()

const isChangingClient = ref(false)
const selectedClientId = ref<string | null>(clientStore.currentClientId)

watch(() => clientStore.currentClientId, (newId) => {
  selectedClientId.value = newId
})

async function onClientChange() {
  const id = selectedClientId.value
  if (!id || id === clientStore.currentClientId) return

  isChangingClient.value = true
  try {
    await userClientService.switchClient(id)
    clientStore.setCurrentClient(id)
  } catch {
    selectedClientId.value = clientStore.currentClientId
    await clientStore.loadClientsFromApi()
  } finally {
    isChangingClient.value = false
  }
}

const toast = useToast()
const showProfileDialog = ref(false)
const showProfileEditModal = ref(false)
const isUpdatingProfile = ref(false)
const currentUser = ref<IUser>({})

const loadCurrentUser = () => {
  const storedUser = ls.get(process.env.VUE_APP_LS_USER_LOGGED_KEY)
  currentUser.value = storedUser ? { ...storedUser, updatedAt: new Date(storedUser.updatedAt) } : {}
}

const openProfileEdit = () => {
  showProfileDialog.value = false
  showProfileEditModal.value = true
}

const handleProfileUpdate = async (formData: any) => {
  isUpdatingProfile.value = true
  try {
    const dto = buildUserDto(formData, currentUser.value)
    const res = await userService.updateSelf(dto)
    currentUser.value = res.data
    ls.set(process.env.VUE_APP_LS_USER_LOGGED_KEY, res.data)
    toast.add({ severity: 'success', summary: languageStore.t('general.myAccount'), detail: languageStore.t('general.profileUpdated'), life: 3000 })
    showProfileEditModal.value = false
  } catch (error: any) {
    toast.add({
      severity: 'error',
      summary: languageStore.t('general.myAccount'),
      detail: error?.response?.data?.detail ?? languageStore.t('general.errorUpdatingProfile'),
      life: 3000,
    })
  } finally {
    isUpdatingProfile.value = false
  }
}

onMounted(() => {
  clientStore.loadClientsFromApi()
  loadCurrentUser()
})

const { isDark } = useDarkMode()

type NavBarMenuItem = MenuItem & {
  multiline?: boolean
  items?: NavBarMenuItem[]
}

const hasChildren = (item: NavBarMenuItem) => Boolean(item.items && item.items.length > 0)
const isMultilineItem = (item: NavBarMenuItem) => Boolean(item.multiline)

const permissionsNavbarRouter = computed(() => {
  const filterRoutes = (routes: INavBarItem[]): INavBarItem[] => {
    return routes

      .map((route): INavBarItem | null => {
        const routeCopy = { ...route }

        if (route.items) {
          const filteredItems: INavBarItem[] = filterRoutes(route.items)

          routeCopy.items = filteredItems

          return filteredItems.length > 0 ? routeCopy : null
        }

        if (!route.hasOwnProperty('requiredPermission')) {
          return routeCopy
        }

        return route.requiredPermission && hasPermission(route.requiredPermission as UserPermission)
          ? routeCopy
          : null
      })
      .filter((item): item is INavBarItem => item !== null)
  }

  return filterRoutes(navBar.routes)
})

const logout = () => userService.logout(router)
const userName = userService.getUserName()
const appVersion = getAppVersion()
const apiVersionLabel = ref('…')

onMounted(() => {
  void (async () => {
    try {
      const { data } = await getBackendVersion()
      apiVersionLabel.value = data.version
    } catch {
      apiVersionLabel.value = '—'
    }
  })()
})


const menuClasses = computed(() => [
  navBar.shown ? 'w-[20rem] grid grid-rows-[auto_1fr_auto]' : 'w-[4rem] flex flex-col',
  'bg-surface-0 dark:bg-surface-900 border-surface gap-4 fixed left-0 top-0 z-50 h-screen border-r p-2 shadow-lg transition-all duration-300',
])
</script>

<style>
.p-panelmenu {
  border: none !important;
  box-shadow: none !important;
  gap: 0 !important;
}
.p-panelmenu-panel {
  border-width: 0px 0px 1px 0px !important;
  border-radius: 0 !important;
  margin: 0 !important;
}
</style>
